/**
 * ColorUtils - Shared Color Management Utility
 * 
 * SOLID Principle: Single Responsibility Principle (SRP) & DRY (Don't Repeat Yourself)
 * Purpose: Consolidates color management logic that was duplicated between
 * ChartConfigBuilder and DirectionalArrowRenderer. Provides consistent color
 * assignment across all chart elements.
 * 
 * Responsibilities:
 * - Maintain standard color palette
 * - Assign consistent colors to fields across components
 * - Handle special color cases (e.g., pCO2 grey)
 * - Provide color utilities for accessibility
 * 
 * Context: Charts need consistent colors for fields across lines, axes, arrows,
 * and tooltips. This utility ensures the same field always gets the same color.
 */

import { LabelUtils } from './LabelUtils.js';

/**
 * Standard color palette for chart elements
 * Colors chosen for good contrast and accessibility
 */
const CHART_COLORS = [
  '#82ca9d', // green - good for temperature, positive trends
  '#8884d8', // purple - neutral, good contrast
  '#ff4c4c', // red - attention-grabbing, good for alerts
  '#00c49f', // teal - calming, good for water-related data
  '#b76fd4', // violet - distinctive, good for derived data
  '#c9b800', // gold - warm, good for environmental data
  '#e67e22', // orange - energetic, good for dynamic data
  '#0077cc', // blue - trustworthy, good for baseline measurements
];

/**
 * Fixed color assignments for specific field types
 * Each field always gets the same color regardless of selection order
 */
const FIXED_FIELD_COLORS = {
  // CO2 data (line is grey; mode colors come through the dot renderer)
  'procv_co2:pCO2': '#6c757d',
  'mini_co2:pCO2': '#6c757d',
  
  // Temperature fields
  'virtual_ctd:temperature_deg_c': '#82ca9d',
  'temperature': '#82ca9d',
  'temp': '#82ca9d',
  
  // Salinity fields
  'virtual_ctd:salinity_psu': '#8884d8',
  'salinity': '#8884d8',
  'sal': '#8884d8',
  
  // Pressure/Depth fields
  'virtual_ctd:pressure_dbar': '#ff4c4c',
  'pressure': '#ff4c4c',
  'depth': '#ff4c4c',
  
  // Wave fields - all distinct colors
  'virtual_waves:hs_m': '#00c49f',              // Sig wave height - teal
  'virtual_waves:dir_mean_deg': '#17a2b8',      // Wave direction - darker teal
  'virtual_waves:hmax_m': '#ff6b9d',            // Wave max height - pink
  'wave_height': '#00c49f',
  'wave_direction': '#17a2b8',
  'wave_max_height': '#ff6b9d',
  'waves': '#00c49f',
  
  
  // Virtual tide fields - distinct colors
  'virtual_tide:water_level_m': '#00D5FF',      // Tide water level - cyan
  'virtual_tide:prediction_m': '#ff5722',       // Tide prediction - deep orange
  'water_level_m': '#00D5FF',
  'prediction_m': '#ff5722',
  
  // Wind fields - different colors for speed vs direction
  'virtual_wind:wind_speed_avg_mps': '#b76fd4',
  'virtual_wind:wind_dir_avg_deg': '#9c27b0',
  'wind_speed': '#b76fd4',
  'wind_direction': '#9c27b0',
  'wind': '#b76fd4',
  
  // Current fields - different colors for speed vs direction
  'virtual_currents:speed_mps': '#FF9933',      
  'virtual_currents:dir_deg': '#FF9933',        // Current direction - yellow
  
  // CTD Oxygen - distinct from dissolved oxygen
  'virtual_ctd:oxygen_mg_l': '#e91e63',         // CTD Oxygen - magenta
  'ctd_oxygen': '#e91e63',
  
  // CTD Sound velocity - distinct color
  'virtual_ctd:sound_velocity_m_s': '#607d8b',  // CTD Sound velocity - blue grey
  'sound_velocity': '#607d8b',
  
  // CTD Conductivity - distinct from salinity and current speed
  'virtual_ctd:conductivity_s_m': '#3f51b5',    // CTD Conductivity - indigo
  'conductivity': '#3f51b5',
  
  // Other environmental
  'dissolved_oxygen': '#e67e22',                // Keep original orange
  'turbidity': '#0077cc',
  
  // Additional unique colors for other common fields
  'ph': '#28a745',
  'chlorophyll': '#20c997',
  'nitrate': '#fd7e14',
  'phosphate': '#6610f2',
  'ammonia': '#dc3545',
  'silicate': '#6f42c1',
  'density': '#495057',
};

export class ColorUtils {
  /**
   * Gets the standard color palette
   * 
   * @returns {Array} Array of color hex codes
   */
  static getColorPalette() {
    return [...CHART_COLORS];
  }

  /**
   * Builds color mapping for a list of fields
   * 
   * Uses fixed field-to-color assignments to ensure consistent colors
   * regardless of selection order.
   * 
   * @param {Array} fields - Field keys to assign colors to
   * @returns {Object} Map of fieldKey -> color
   */
  static buildColorMapping(fields) {
    const colorMap = {};
    
    fields.forEach((fieldKey) => {
      colorMap[fieldKey] = this.getFieldColor(fieldKey);
    });
    
    return colorMap;
  }

  /**
   * Gets color for a specific field using fixed assignment
   * 
   * @param {string} fieldKey - Field identifier
   * @param {number} index - Fallback index (ignored, kept for compatibility)
   * @returns {string} Hex color code
   */
  static getFieldColor(fieldKey, index = 0) {
    // Extract base field key for color lookup (remove buoy prefix if present)
    const baseFieldKey = LabelUtils.getBaseFieldKey(fieldKey);
    
    // Check fixed field colors first using base field key
    if (FIXED_FIELD_COLORS[baseFieldKey]) {
      return FIXED_FIELD_COLORS[baseFieldKey];
    }
    
    // For unknown fields, generate consistent color based on base field name hash
    // Use base field key so that CT1002_procv_co2:pCO2 and CT1003_procv_co2:pCO2 get same color
    return this.getColorFromFieldName(baseFieldKey);
  }

  /**
   * Generates a consistent color for unknown fields based on field name
   * 
   * @param {string} fieldKey - Field identifier
   * @returns {string} Hex color code
   */
  static getColorFromFieldName(fieldKey) {
    // Create a simple hash from the field name
    let hash = 0;
    for (let i = 0; i < fieldKey.length; i++) {
      const char = fieldKey.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    
    // Use absolute value and modulo to get consistent palette index
    const paletteIndex = Math.abs(hash) % CHART_COLORS.length;
    return CHART_COLORS[paletteIndex];
  }

  /**
   * Registers a special color for a field type
   * 
   * Allows extending color system without modifying existing code
   * 
   * @param {string} fieldKey - Field identifier
   * @param {string} color - Hex color code
   */
  static registerSpecialColor(fieldKey, color) {
    FIXED_FIELD_COLORS[fieldKey] = color;
  }

  /**
   * Gets contrasting text color for a given background color
   * 
   * @param {string} backgroundColor - Hex color code
   * @returns {string} 'white' or 'black' for optimal contrast
   */
  static getContrastingTextColor(backgroundColor) {
    // Remove # if present
    const hex = backgroundColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return contrasting color
    return luminance > 0.5 ? 'black' : 'white';
  }

  /**
   * Converts hex color to rgba with specified opacity
   * 
   * @param {string} hex - Hex color code
   * @param {number} opacity - Opacity value (0-1)
   * @returns {string} RGBA color string
   */
  static hexToRgba(hex, opacity = 1) {
    // Remove # if present
    const cleanHex = hex.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(cleanHex.substr(0, 2), 16);
    const g = parseInt(cleanHex.substr(2, 2), 16);
    const b = parseInt(cleanHex.substr(4, 2), 16);
    
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  /**
   * Gets color palette suitable for directional data (arrows)
   * 
   * Returns colors that work well for arrow visualization
   * 
   * @returns {Array} Array of colors optimized for arrows
   */
  static getDirectionalColorPalette() {
    // Use subset of main palette that works well for arrows
    return [
      '#8884d8', // purple
      '#00c49f', // teal
      '#e67e22', // orange
      '#0077cc', // blue
      '#c9b800', // gold
    ];
  }

  /**
   * Validates if a color is in valid hex format
   * 
   * @param {string} color - Color string to validate
   * @returns {boolean} True if valid hex color
   */
  static isValidHexColor(color) {
    const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
    return hexPattern.test(color);
  }
}

export default ColorUtils;